// Copyright (C) 2021-Present CITEC Inc. <https://citecsolutions.com/>
// All rights reserved
//
// This file is part of CITEC Inc. source code.
// This software framework contains the confidential and proprietary information
// of CITEC Inc., its affiliates, and its licensors. Your use of these
// materials is governed by the terms of the Agreement between your organization
// and CITEC Inc., and any unauthorized use is forbidden. Except as otherwise
// stated in the Agreement, this software framework is for your internal use
// only and may only be shared outside your organization with the prior written
// permission of CITEC Inc.
// CITEC Inc. source code can not be copied and/or distributed without the express
// permission of CITEC Inc.
import { useCallback, useState, useMemo, type FC } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  CircularProgress,
} from '@mui/material';
import { Translation } from '@shared/ui-kit/providers/translation-provider';
import {
  AppIcon,
  Input,
  Label,
  OptionsSelector,
  DateInput,
} from '@shared/ui-kit/components';
import type { UserProfileData } from './use-user-profile';
import { useTranslation } from 'react-i18next';

export interface EditUserProfileModalProps {
  open: boolean;
  initialValues: UserProfileData;
  onClose: () => void;
  onSave: (data: UserProfileData) => Promise<void>;
  isUpdating: boolean;
}

export const EditUserProfileModal: FC<EditUserProfileModalProps> = ({
  open,
  initialValues,
  onClose,
  onSave,
  isUpdating,
}) => {
  const [values, setValues] = useState<UserProfileData>(initialValues);
  const { t } = useTranslation();
  const countryOptions = useMemo(() => {
    return [
      {
        value: 'spain',
        label: t('personal-data.countries.spain' as any, { ns: 'components' }),
      },
      {
        value: 'france',
        label: t('personal-data.countries.france' as any, { ns: 'components' }),
      },
      {
        value: 'germany',
        label: t('personal-data.countries.germany' as any, {
          ns: 'components',
        }),
      },
      {
        value: 'italy',
        label: t('personal-data.countries.italy' as any, { ns: 'components' }),
      },
      {
        value: 'portugal',
        label: t('personal-data.countries.portugal' as any, {
          ns: 'components',
        }),
      },
      {
        value: 'uk',
        label: t('personal-data.countries.uk' as any, { ns: 'components' }),
      },
      {
        value: 'belgium',
        label: t('personal-data.countries.belgium' as any, {
          ns: 'components',
        }),
      },
      {
        value: 'netherlands',
        label: t('personal-data.countries.netherlands' as any, {
          ns: 'components',
        }),
      },
      {
        value: 'other',
        label: t('personal-data.countries.other' as any, { ns: 'components' }),
      },
    ];
  }, [t]);
  const handleChange = useCallback(
    (field: keyof UserProfileData) => (e: React.ChangeEvent<HTMLInputElement>) => {
      const v = e.target.value;
      setValues(prev => ({ ...prev, [field]: v }));
    },
    [setValues],
  );
  const handleCountryChange = useCallback(
    (v: string) => {
      setValues(prev => ({ ...prev, country: v }));
    },
    [setValues],
  );
  const handleSubmit = useCallback(
    async (e: React.FormEvent) => {
      e.preventDefault();

      const hasChanges = Object.keys(values).some(
        key =>
          values[key as keyof UserProfileData] !==
          initialValues[key as keyof UserProfileData],
      );

      if (!hasChanges) {
        onClose();
        return;
      }

      await onSave(values);

      onClose();
    },
    [onClose, onSave, values, initialValues],
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      aria-labelledby="edit-profile-title"
      PaperProps={{ className: '!bg-page' }}
    >
      <form onSubmit={handleSubmit} id="edit-profile-form">
        <DialogTitle id="edit-profile-title">
          <Translation i18nKey="user-profile.personal-data" ns="components" />
        </DialogTitle>
        <DialogContent dividers sx={{ px: 4 }}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="first-name-input">
                  <Translation i18nKey="user-profile.first-name" ns="components" />
                </Label>
                <Input
                  id="first-name-input"
                  name="first_name"
                  value={values.first_name ?? ''}
                  onChange={handleChange('first_name')}
                  data-testid="first-name-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="second-name-input">
                  <Translation i18nKey="user-profile.second-name" ns="components" />
                </Label>
                <Input
                  id="second-name-input"
                  name="second_name"
                  value={values.second_name ?? ''}
                  onChange={handleChange('second_name')}
                  data-testid="second-name-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="first-surname-input">
                  <Translation
                    i18nKey="user-profile.first-surname"
                    ns="components"
                  />
                </Label>
                <Input
                  id="first-surname-input"
                  name="first_surname"
                  value={values.first_surname ?? ''}
                  onChange={handleChange('first_surname')}
                  data-testid="first-surname-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="second-surname-input">
                  <Translation
                    i18nKey="user-profile.second-surname"
                    ns="components"
                  />
                </Label>
                <Input
                  id="second-surname-input"
                  name="second_surname"
                  value={values.second_surname ?? ''}
                  onChange={handleChange('second_surname')}
                  data-testid="second-surname-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="email-input">
                  <Translation i18nKey="user-profile.email" ns="components" />
                </Label>
                <Input
                  id="email-input"
                  name="email"
                  value={values.email}
                  disabled
                  data-testid="email-input"
                />
                <span className="text-xs text-gray-500">
                  <Translation
                    i18nKey="user-profile.email-not-editable"
                    ns="components"
                  />
                </span>
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="phone-input">
                  <Translation i18nKey="user-profile.phone" ns="components" />
                </Label>
                <Input
                  id="phone-input"
                  name="phone_number"
                  value={values.phone_number as string}
                  onChange={handleChange('phone_number')}
                  data-testid="phone-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="address-input">
                  <Translation i18nKey="user-profile.address" ns="components" />
                </Label>
                <Input
                  id="address-input"
                  name="address"
                  value={values.address as string}
                  onChange={handleChange('address')}
                  data-testid="address-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={4}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="postal-code-input">
                  <Translation
                    i18nKey="onboarding-personal-data-basic-info.postalCode"
                    ns="components"
                  />
                </Label>
                <Input
                  id="postal-code-input"
                  name="postal_code"
                  value={values.postal_code as string}
                  onChange={handleChange('postal_code')}
                  data-testid="postal-code-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={4}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="city-input">
                  <Translation
                    i18nKey="onboarding-personal-data-basic-info.city"
                    ns="components"
                  />
                </Label>
                <Input
                  id="city-input"
                  name="city"
                  value={values.city as string}
                  onChange={handleChange('city')}
                  data-testid="city-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={4}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="country-input">
                  <Translation i18nKey="user-profile.country" ns="components" />
                </Label>
                <OptionsSelector
                  optionsAvailable={countryOptions}
                  onChangeValueSelected={handleCountryChange}
                  optionSelected={values.country as string}
                  defaultText={t('common.country-placeholder', { ns: 'common' })}
                  selectType="white"
                  data-testid="country-input"
                />
              </fieldset>
            </Grid>
            <Grid item xs={12} sm={6}>
              <fieldset className="flex flex-col gap-2">
                <Label htmlFor="birth-date-input">
                  <Translation i18nKey="user-profile.birth-date" ns="components" />
                </Label>
                <DateInput
                  id="birth-date-input"
                  name="birth-date"
                  value={values.birth_date as string}
                  onChange={value =>
                    setValues(prev => ({ ...prev, birth_date: value }))
                  }
                  data-testid="birth-date-input"
                />
              </fieldset>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }} className="!gap-4">
          <Button
            onClick={() => {
              onClose();
              setValues(initialValues);
            }}
            color="secondary"
            variant="outlined"
            disabled={isUpdating}
            startIcon={<AppIcon icon="Close" />}
          >
            <Translation i18nKey="common.cancel" ns="common" />
          </Button>
          <Button
            color="secondary"
            variant="filled"
            type="submit"
            disabled={isUpdating}
            startIcon={<AppIcon icon="Save" size="lg" />}
          >
            <Translation i18nKey="user-profile.save" ns="components" />
          </Button>
          {isUpdating && (
            <div className="ml-2 mt-2">
              <CircularProgress size={24} />
            </div>
          )}
        </DialogActions>
      </form>
    </Dialog>
  );
};
